import React, { useEffect, useState } from "react"

import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import { useSignUpModalState } from "@fuse/sharedComponents/globalQuickSignUpModal"
import InputField from "@fuse/sharedComponents/inputField"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import { EButtonType, EPlatform, TWebUser } from "@letshang/constants"
import { useRecoilState } from "recoil"

import { updateUserStateFromBackend, userState } from "@fuse/recoil/signup"

import { updateUserDetails } from "@api/firebaseWrappedApis"

import { useEvents } from "@fuse/pages/eventPage/controller"

const NameComponent = ({
  closeModal,
  from = "joinEvent",
  verifyEmail = false,
  setStep,
}: {
  closeModal?: () => void
  from?: "createEvent" | "joinEvent"
  verifyEmail?: boolean
  setStep?: (step: number) => void
}) => {
  const [user, setUser] = useRecoilState(userState)
  const [name, setTheName] = useState<string>("")

  const [isLoading, setIsLoading] = useState(false)

  const handleOnChange = (value: string) => {
    setUser((prev: TWebUser) => ({ ...prev, name: value }))
  }
  const { setFastSignUpStage, setEventParams } = useSignUpModalState()
  const { plusOnePopupState, setPlusOnePopupState, plusOnes } = useEvents(
    user.eventId,
  )

  const plusOneAllowedNumber = plusOnes?.allowedNumber ?? [0, 1]
  const isAllowed = plusOnes?.isAllowed ?? false

  const min = plusOneAllowedNumber[0]
  const max = plusOneAllowedNumber[1]

  const handleJoinEvent = async () => {
    const userObj = {
      name: name,
      expoPushToken: user.expoPushToken,
      country: user.country,
      phoneNumber: user.phone,
      firstName: name,
    }
    try {
      setIsLoading(true)
      const res: any = await updateUserDetails({
        ...userObj,
        eventId: user.eventId,
        //   added missing dependency to avoid the ts error
        id: "",
        email: "",
        phone: "",
        dateOfBirth: "",
        profileImage: "",
        events: [],
        platform: EPlatform.WEB,
      })

      if (res.success) {
        await updateUserStateFromBackend()
        setUser((prev: TWebUser) => ({
          ...prev,
          isOnboarded: true,
          isLoggedIn: true,
        }))
        setEventParams({ plusOneCount: plusOnePopupState.plusOneCount })
        if (verifyEmail) {
          setStep(4)
          return
        }
        setFastSignUpStage("done")
        closeModal()
      } else {
        console.error("Error updating user details")
        showErrorToast(
          "Unexpected error occurred, please close this window and try again. If the problem persists, please contact support.",
        )
      }
    } catch (e) {
      console.error(e)
      showErrorToast(
        "Unexpected error occurred, please close this window and try again. If the problem persists, please contact support.",
      )
    } finally {
      setIsLoading(false)
    }
  }

  const [err, setError] = useState("")

  const handleSubmit = () => {
    if (name.length < 3) {
      setError("Please enter name of 3 character")
    } else {
      handleJoinEvent()
    }
  }

  useEffect(() => {
    if (name && err) {
      handleOnChange(name)
      setError("")
    }
  }, [name])

  const getCtaBtnTitle = (from: string) => {
    switch (from) {
      case "createEvent":
        return "Create"
      case "joinEvent":
        return "Join"
      default:
        return "Join"
    }
  }

  return (
    <div className="flex gap-6 flex-col">
      <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
      <div className="text-left">
        <p className="text-white/35 text-sm mb-1">Your name</p>
        <div className="px-4 bg-white/20 h-12 rounded-md flex w-full items-center">
          <input
            type="text"
            placeholder="John Doe"
            className="w-full h-full bg-transparent text-white text-[15px] placeholder-white/35 outline-none"
            autoFocus
            value={name}
            onChange={(e) => setTheName(e.target.value)}
          />
        </div>
        {err && (
          <div className={`text-[red] text-left font-medium text-xs py-1 `}>
            {err}
          </div>
        )}
        {from === "joinEvent" ? (
          <p className="text-sm text-white/35 opacity-50 mt-1 mb-8">
            The host would love to know your name so they can spot you easily.
          </p>
        ) : null}
        {isAllowed && (
          <div>
            <p className="text-white/35 text-sm mb-1">Add a +1 (optional)</p>
            <select
              className="w-full p-3 rounded-lg bg-white/30 backdrop-blur-md text-white border-none focus:outline-none"
              value={plusOnePopupState.plusOneCount}
              onChange={(e) => {
                setPlusOnePopupState((prev) => ({
                  ...prev,
                  plusOneCount: Number(e.target.value),
                }))
              }}
            >
              {Array.from({ length: max - min + 1 }, (_, i) => (
                <option key={i} value={min + i}>
                  {min + i}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <Button
        variant={EButtonType.solid}
        className="py-2 mt-6"
        onClick={handleSubmit}
      >
        <div className="text-white font-semibold font-sans">
          {getCtaBtnTitle(from)}
        </div>
      </Button>
    </div>
  )
}

export default NameComponent
